// COPIA DICHIARATA di src/lib/scopa/useAzioneScopa.ts @ e99b95115c26c5085335a527db6cb22b23fd2f81 (TMA#735, 2026-10-20)
// Cosa cambia rispetto alla fonte: il nome (`useAzioneTavolo`) e i nomi che importa da `rest.ts` (`guastoOf`, `idempotencyKey`, `teniamoLaChiave`, `GuastoTavolo`).
// Il codice e IDENTICO.
import { useCallback, useRef, useState } from 'react';

import { guastoOf, idempotencyKey, teniamoLaChiave, type GuastoTavolo } from './rest';

/** `rispostaStrana`: un 2xx che non ha la forma attesa (la scrittura puo essere andata: se ne parla senza ritentare da soli). */
export type GuastoAzione = GuastoTavolo | 'rispostaStrana';

export interface StatoAzione {
  /** `true` mentre la richiesta e in volo: i bottoni si spengono (e la guardia sta a parte, in un ref). */
  invio: boolean;
  guasto: GuastoAzione | null;
}

/**
 * UNA scrittura di un tavolo (crea, entra, rivincita): un'azione dell'utente = una chiave di idempotenza + una sola richiesta in volo.
 *
 * 🔴 Il doppio tocco non crea due tavoli, in due modi indipendenti: (1) `inVolo` e un REF, scritto in modo sincrono (il bottone spento e comodita, non la guardia);
 * (2) la chiave di idempotenza, che il game host lega alla persona: una ripetizione della STESSA azione dopo una risposta persa ritrova lo stesso tavolo.
 * La chiave: nuova alla prima richiesta di un'azione, la STESSA a ogni ripetizione finche l'esito e incerto; si scarta dopo un successo e dopo un rifiuto definitivo.
 * Ogni azione ha il SUO hook (e la sua chiave).
 */
export function useAzioneTavolo<R>() {
  const chiave = useRef<string | null>(null);
  const inVolo = useRef(false);
  const [stato, setStato] = useState<StatoAzione>({ invio: false, guasto: null });

  const esegui = useCallback(async (chiamata: (idempotencyKey: string) => Promise<R | null>): Promise<R | null> => {
    if (inVolo.current) return null; // il doppio tocco: la prima richiesta e ancora in volo
    inVolo.current = true;
    setStato({ invio: true, guasto: null });
    chiave.current ??= idempotencyKey();
    try {
      const esito = await chiamata(chiave.current);
      if (esito === null) {
        setStato({ invio: false, guasto: 'rispostaStrana' });
        return null;
      }
      chiave.current = null;
      setStato({ invio: false, guasto: null });
      return esito;
    } catch (e) {
      if (!teniamoLaChiave(e)) chiave.current = null;
      setStato({ invio: false, guasto: guastoOf(e) });
      return null;
    } finally {
      inVolo.current = false;
    }
  }, []);

  const azzera = useCallback(() => setStato({ invio: false, guasto: null }), []);
  return { stato, esegui, azzera };
}
